import { get, post } from '@/util/request.js';
import { createPatch } from 'rfc6902';
import type { EAV } from '@fibl/zdb-common';
import { LRUCache } from 'lru-cache';
import { getI18n } from '@/util/i18n';

export function update<T extends Record<string, unknown>>(
	oldEntity: EAV.Entity<T>,
	newData: T,
	changeset?: string,
	surpressError?: boolean,
): Promise<EAV.Entity<T>> {
	const i18n = getI18n();
	if (!oldEntity) {
		throw new Error(i18n.t('repository.missingOld'));
	}
	const patch = createPatch(oldEntity.data, newData);
	if (patch.length === 0) {
		return Promise.resolve(oldEntity);
	}
	const payload: Record<string, any> = {
		id: oldEntity.id,
		rev: oldEntity.rev,
		patch,
	};
	if (changeset && changeset !== oldEntity.attributeset) {
		payload.changeset = changeset;
	}
	return post('eav/entity/updatefrompatch', payload, null, surpressError);
}

export function updateFromPatch(
	id: string,
	rev: number,
	patch: EAV.JsonPatch,
): Promise<EAV.Entity<any>> {
	const payload: Record<string, any> = { id, rev, patch };
	return post('eav/entity/updatefrompatch', payload);
}

export function create<T extends Record<string, unknown>>(
	entityclass: string,
	attributeset: string,
	data: Record<string, any>,
): Promise<EAV.Entity<T>> {
	return post('eav/entity/create', {
		entityclass,
		attributeset,
		data,
	});
}

export function remove(id: string) {
	return post('eav/entity/delete', { id });
}

export function setAttachment<T extends Record<string, unknown>>(
	id: string,
	rev: number,
	attachment: string,
): Promise<EAV.Entity<T>> {
	return post('eav/entity/setattachment', {
		id,
		rev,
		attachment,
	});
}

export function getAttachment(id: string, rev?: number): Promise<string> {
	return get('eav/entity/getattachment', { id, rev });
}

export function getEntityTimeline(id: string): Promise<EAV.EntityHistoryResponse> {
	return get('eav/entity/history', { id });
}

const historyCache = new LRUCache({
	max: 500,
	ttl: 1000 * 60 * 15,
	ttlAutopurge: false,
});

export function getEntityByIdDate<T extends Record<string, unknown> = any>(
	id: string,
	date: string,
): Promise<EAV.Entity<T>> {
	if (date.length === 10) date = `${date} 23:00:00`;
	let key = `${id}__${date}`;
	let cached = historyCache.get(key);
	if (cached) return cached as any;
	const p = get('eav/entity/get', { id, date });
	historyCache.set(key, p);
	return p;
}
